import { Inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Title, Meta } from '@angular/platform-browser';
import { SiteMeta } from '../entity/site-meta';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../../generator-env-token';
import { Template } from '../entity/template';
import { Menu, MenuSub } from '../entity/menus';
import { CommonHttpClientService } from 'src/app/share/service/common-http-client.service';
import { AbstractTemplateService } from '../entity/abstract-template-service';
import { ServerConfigService } from 'src/app/share/service/server-config.service';
import { ISiteInfo } from '../entity/siteInfo';
import { BackStageTemplateService, TemplateType } from 'src/app/share/service/back-stage-template.service';
import { SiteSeries } from 'src/app/theme-sites/SiteSeries';
import { CollectionSubject } from '../entity/collection-subject';
import { IndexCollection } from '../../index-section-generator/module/index-collection/index-collection';
import { TranslateService } from '@ngx-translate/core';
import { generateSectionDefaultData, generateTemplateDefaultData } from '../entity/template-default-data';

@Injectable({
  providedIn: 'root'
})
export class TemplateService extends AbstractTemplateService<Template> {
  /** 整個網站的基本資料 */
  private _siteInfo: ISiteInfo;
  /** 整個網站的基本資料 */
  siteInfo$ = new ReplaySubject<ISiteInfo>(undefined);
  /** 整個網站的基本資料 */
  get siteInfo() { return this._siteInfo; }
  /** 網站中繼標籤設定資料 */
  get siteMeta(): SiteMeta { return this.siteInfo ? this.siteInfo.metadata : null; }
  /** 網站已被使用者允許播放聲音 */
  hasAllowSound = false;
  /** 網站已成功自動撥放任一支影片 */
  hasFirstVideoPlayed = false;

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) private generatorEnv: GeneratorEnv,
    private http: CommonHttpClientService,
    private titleService: Title,
    private metaService: Meta,
    private serverConfigService: ServerConfigService,
    private backStageTemplateService: BackStageTemplateService,
    private translateService: TranslateService,
  ) {
    super();
    this.backStageTemplateService.registTemplateService(this, TemplateType.index);
  }

  /** 設定頁面要套用的模板key */
  public setTemplateKey(templateKey: string) {
    // 取得所有的網站基本資料
    this.serverConfigService.getConfig$().pipe(
      tap(info => { // 決定版型用哪個
        const applyTemplate = templateKey || info.templateKey;
        const isTemplateSame = applyTemplate === info.template.selector; // 選擇的版型是否一致
        const defaultTmp = generateTemplateDefaultData(applyTemplate || 'template-040') as Template; // 模板預設資料
        if (isTemplateSame) { // 沒有換版
          if (!info.template) { // 還沒有設定過首頁資訊
          info.template = Object.assign(new Template(), defaultTmp);
          } else { // 已經設定過首頁資訊
          info.template = Object.assign(new Template(), defaultTmp, info.template);
            info.template.data = Object.assign(defaultTmp.data, info.template.data);
        }
        } else { // 使用者換過版
          // 直接換成另一個版的預設資料
          info.template = Object.assign(new Template(), defaultTmp);
        }

        // sections 多退少補
        if (defaultTmp.sections && defaultTmp.sections.length) {
          if (info.template.sections && info.template.sections.length) {
            const baseArr = defaultTmp.sections.slice();
            const rmtArr = info.template.sections.slice();
            const resArr = [];
            for (let j = 0 ; j < rmtArr.length ; j++) {
              const fidx = baseArr.findIndex(sec => sec.selector === rmtArr[j].selector);
              if (fidx !== -1) {
                const defaultData = generateSectionDefaultData(rmtArr[j].selector);
                rmtArr[j].data = Object.assign(defaultData.data, rmtArr[j].data);
                resArr.push(rmtArr[j]);
              }
            }
            for (let i = 0 ; i < baseArr.length ; i++) {
              const fidx = rmtArr.findIndex(sec => sec.selector === baseArr[i].selector);
              if (fidx === -1) {
                resArr.splice(i, 0, baseArr[i]);
              }
            }
            info.template.sections = resArr;
            info.template.sections.forEach((section, secIdx) => {
              section.data.index = secIdx;
            });
          } else {
            info.template.sections = defaultTmp.sections;
          }
        } else if (info.template.sections && info.template.sections.length) {
          info.template.sections = [];
        }
      }),
      tap(info => { // 設定 SEO META
        const siteMeta: SiteMeta = info.metadata;
        if (this.titleService.getTitle() === '' || this.titleService.getTitle() === '國家文化記憶庫2.0') {
          this.titleService.setTitle(siteMeta.title);
        }
        this.setMeta(info);
      }),
    ).subscribe(info => {
      this._template = info.template;
      this._siteInfo = info;
      this.siteInfo$.next(info);
    });
  }

  private setMeta(siteInfo: ISiteInfo) {
    const siteMeta = siteInfo.metadata;
    if (siteMeta) {
      this.metaService.updateTag({ property: 'og:site_name', content: siteMeta.title });
      if (this.metaService.getTag('name=title').content === '' || this.metaService.getTag('name=title').content === '國家文化記憶庫2.0') {
        this.metaService.updateTag({ name: 'title', content: siteMeta.title });
        this.metaService.updateTag({ property: 'og:title', content: siteMeta.title });
        this.metaService.updateTag({ name: 'twitter:title', content: siteMeta.title });
      }
      if (this.metaService.getTag('name=description').content === '' || this.metaService.getTag('name=description').content === '臺灣歷史博物館承接臺灣從古自今數位典藏使命，國家文化記憶庫2.0匯集國內許多重要博物館與典藏機關的數位素材、開放資料，從臺灣史事到歷年新聞、從傳統技藝到現代工藝、從原住民到新住民的文化傳承，逐步累積並開放彰顯臺灣特色的文化內容。國家文化記憶庫2.0除了提供容易搜尋的平臺介面與明確授權標示，更由專業文史工作者創作各種文化主題內容，不禁能喚起你的文化記憶，更能發掘到更多你所不知道的臺灣。') {
        this.metaService.updateTag({ name: 'description', content: siteMeta.description });
        this.metaService.updateTag({ property: 'og:description', content: siteMeta.description });
        this.metaService.updateTag({ name: 'twitter:description', content: siteMeta.description });
      }
      if (this.metaService.getTag('name=image').content === '' || this.metaService.getTag('name=image').content === '/assets/images/BCM1024X500.jpg') {
        this.metaService.updateTag({ name: 'image', content: siteMeta.thumbnailFile[0].url });
        this.metaService.updateTag({ property: 'og:image', content: siteMeta.thumbnailFile[0].url });
        this.metaService.updateTag({ name: 'twitter:image', content: siteMeta.thumbnailFile[0].url });
      }
      this.metaService.updateTag({ name: 'keywords', content: siteMeta.keywords.join(', ') });
    }
    this.metaService.updateTag({ name: 'google-site-verification', content: this.generatorEnv.googleSiteVerification });
  }

  setHasAllowSoundPlay(allow: boolean) {
    this.hasAllowSound = allow;
  }

  getHasAllowSoundPlay() {
    return this.hasAllowSound;
  }

  setHasFirstVideoPlayed(isPlayed: true) {
    this.hasFirstVideoPlayed = isPlayed;
  }

  getHasFirstVideoPlayed() {
    return this.hasFirstVideoPlayed;
  }

  /**
   * 設置網頁的描述性標題
   */
  setWebTitle(meta: { title?: string; desc?: string; image?: string; }) {
    // console.trace('TemplateService.prototype.setWebTitle: ' + JSON.stringify(meta));
    if ('title' in meta) {
      if (!meta.title) {
        meta.title = this.siteMeta ? this.siteMeta.title : '';
      }
      this.titleService.setTitle(meta.title);
      this.metaService.updateTag({ name: 'title', content: meta.title });
      this.metaService.updateTag({ property: 'og:title', content: meta.title });
      this.metaService.updateTag({ name: 'twitter:title', content: meta.title });
    }

    if ('desc' in meta) {
      if (!meta.desc) {
        meta.desc = this.siteMeta ? this.siteMeta.description : '';
      }
      this.metaService.updateTag({ name: 'description', content: meta.desc });
      this.metaService.updateTag({ property: 'og:description', content: meta.desc });
      this.metaService.updateTag({ name: 'twitter:description', content: meta.desc });
    }

    if ('image' in meta) {
      if (!meta.image) {
        meta.image = this.siteMeta ? this.siteMeta.thumbnailFile[0].url : '';
      }
      this.metaService.updateTag({ name: 'image', content: meta.image });
      this.metaService.updateTag({ property: 'og:image', content: meta.image });
      this.metaService.updateTag({ name: 'twitter:image', content: meta.image });
    }
  }

  /** 取得網頁的描述性標題 */
  getWebTitle() {
    return this.titleService.getTitle();
  }

  /** 取得網站名稱 */
  getSiteName() {
    const nameMeta = this.metaService.getTag('property="og:site_name"');
    if (nameMeta) {
      return nameMeta.content;
    }
    return '';
  }

  /**
   * 轉換 sitemap
   * 空選單的連結改成第一個有效的子選單連結
   */
  toReliableSiteMap(siteMap: Menu[]): any[] {
    const newSiteMap = [];
    if (siteMap && siteMap.forEach) {
      siteMap.forEach((val: any) => {
        const map2: any = {
          module: val.module,
          title: val.title
        };
        if (val.menus) {
          map2.menus = this.toReliableSiteMap(val.menus);
        }
        if (val.path) {
          map2.path = val.path;
        } else if (val.module === 'EMPTY') {
          let path = '';
          if (val.menus && val.menus.length > 0) {
            const m = val.menus[0];
            if (m.module !== 'EMPTY') {
              path = m.path;
            }
            if (m.module === 'LINKING') {
              map2.module = 'LINKING';
            }
          }
          map2.path = path;
        }
        newSiteMap.push(map2);
      });
    }
    return newSiteMap;
  }

  /** 提交首頁編輯模板設定 */
  updateTemplate(): Observable<Template> {
    const obsFunc = () => {
      return this.http.post<any>(`${this.generatorEnv.apiHost}/backend/api/${this.translateService.currentLang}/index`, {template: this.template}, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(this.generatorEnv.accessTokenKey)}`
        }
      }).pipe(
        catchError((error, observable) => this._errorHandler<any>(error, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得素材參數-主題分類
   * [swagger](http://theme.data.goodays.cc:8106/bcm/data/swagger-ui/index.html#/%E5%BE%8C%E5%8F%B0%E7%9B%B8%E9%97%9C%E5%8F%83%E6%95%B8/fetchCollectionParamSubject)
   */
  getCollectionSubjects(): Observable<CollectionSubject[]> {
    const obsFunc = () => {
      return this.http.get<CollectionSubject[]>(`${this.generatorEnv.apiHost}/backend/api/common/collection/param/subject`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(this.generatorEnv.accessTokenKey)}`
        }
      }).pipe(
        catchError((error, observable) => this._errorHandler<any>(error, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得素材主題下的素材清單
   * [swagger](http://theme.data.goodays.cc:8106/bcm/data/swagger-ui/index.html#/%E5%BE%8C%E5%8F%B0%E8%AA%9E%E7%B3%BB%E7%86%B1%E9%96%80%E7%B4%A0%E6%9D%90/fetch_1)
   */
  getCollectionsInSubject(subjectId: number): Observable<IndexCollection[]> {
    const obsFunc = () => {
      return this.http.get<IndexCollection[]>(`${this.generatorEnv.apiHost}/backend/api/${this.translateService.currentLang}/collectionRand/${subjectId}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(this.generatorEnv.accessTokenKey)}`
        }
      }).pipe(
        catchError((error, observable) => this._errorHandler<any>(error, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 移除素材主題下的幾筆素材
   * [swagger](http://theme.data.goodays.cc:8106/bcm/data/swagger-ui/index.html#/%E5%BE%8C%E5%8F%B0%E8%AA%9E%E7%B3%BB%E7%86%B1%E9%96%80%E7%B4%A0%E6%9D%90/delete_4)
   */
  rmCollectionsInSubject(subjectId: number, collections: IndexCollection[]): Observable<IndexCollection[]> {
    const obsFunc = () => {
      return this.http.delete<IndexCollection[]>(`${this.generatorEnv.apiHost}/backend/api/${this.translateService.currentLang}/collectionRand/${subjectId}`, {
        body: collections.map(collection => ({fetchIndex: collection.fetchIndex, materialId: collection.materialId})),
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(this.generatorEnv.accessTokenKey)}`
        }
      }).pipe(
        catchError((error, observable) => this._errorHandler<any>(error, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 移除素材主題下的所有素材
   */
  rmAllCollectionsInSubject(subjectId: number): Observable<IndexCollection[]> {
    return this.updateCollectionsInSubject(subjectId, []);
  }

  /** 更新素材主題下的素材清單
   * [swagger](http://theme.data.goodays.cc:8106/bcm/data/swagger-ui/index.html#/%E5%BE%8C%E5%8F%B0%E8%AA%9E%E7%B3%BB%E7%86%B1%E9%96%80%E7%B4%A0%E6%9D%90/reset)
   */
  updateCollectionsInSubject(subjectId: number, collections: IndexCollection[]): Observable<IndexCollection[]> {
    const obsFunc = () => {
      return this.http.post<IndexCollection[]>(`${this.generatorEnv.apiHost}/backend/api/${this.translateService.currentLang}/collectionRand/${subjectId}/reset`,
        collections.map(collection => ({fetchIndex: collection.fetchIndex, materialId: collection.materialId})), {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(this.generatorEnv.accessTokenKey)}`
        }
      }).pipe(
        catchError((error, observable) => this._errorHandler<any>(error, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 更新胖胖頁腳設定 */
  updateFatFooterStatus(isShow: boolean) {
    this.template.showFooter = isShow;
    this.keepConnection();
  }

  /** 取得區塊可參考選單路徑 */
  getIndexDataSource(moduleKeys: Array<string>): Observable<MenuSub[]> {
    const obsFunc = () => {
      return this.http.get<MenuSub[]>(`${this.generatorEnv.apiHost}/backend/api/${this.translateService.currentLang}/menu/list`, {
        params: {
          moduleKeys,
        },
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(this.generatorEnv.accessTokenKey)}`
        }
      }).pipe(
        catchError((error, observable) => this._errorHandler<any>(error, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得網站系列 */
  getSiteSeries(): Observable<Array<SiteSeries>> {
    return this.http.get('/api/siteSeries');
  }

  /** 要求維持連線 */
  keepConnection() {
    this.backStageTemplateService.keepConnection();
  }
}
